import type { WalletBalances } from '@/queries/walletBalances.query'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import type { Network } from '@vaultsfyi/common/types'
import { formatAmount } from '@vaultsfyi/common/utils'
import { useCallback, useMemo, useState } from 'react'
import { formatUnits } from 'viem'
import { Button } from '../common/button'
import { NetworkImage } from '../common/networkImage'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '../common/popover'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { ArrowIcon } from '../icons/arrow'
import { CheckIcon } from '../icons/check'

interface ChainSelectorProps {
  chains: Network[]
  selectedChain: Network
  onChange: (chain: Network) => void
  label: string
  disabled?: boolean
  className?: string
  balances?: WalletBalances
}

export const ChainSelector = ({
  chains,
  selectedChain,
  onChange,
  label,
  disabled,
  className,
  balances,
}: ChainSelectorProps) => {
  const [open, setOpen] = useState(false)

  const balanceByChain = useMemo(() => {
    return (balances ?? []).reduce(
      (acc, balance) => {
        if (balance.symbol === 'USDC') {
          const formatted = formatUnits(balance.balanceNative, balance.decimals)
          const numericBalance = Number(formatted)
          if (numericBalance > 0) {
            acc[balance.network.name] = formatAmount(numericBalance, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          }
        }
        return acc
      },
      {} as Record<Network, string>
    )
  }, [balances])

  const onChainChange = useCallback(
    (value: Network) => {
      onChange(value)
      setOpen(false)
    },
    [onChange]
  )

  return (
    <div className="flex w-full flex-col gap-2">
      <label className="font-medium text-body-foreground text-sm">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="input"
            size="default"
            disabled={disabled}
            className={cn(
              'group h-12 justify-between gap-2 px-3 text-left',
              'data-[state=open]:border-body-foreground data-[state=open]:bg-input-hover',
              className
            )}
          >
            <div className="flex w-full items-center gap-2">
              <NetworkImage network={selectedChain} className="size-6 p-0" />
              <span className="text-heading-foreground capitalize">{getNetworkName(selectedChain)}</span>
              {balanceByChain[selectedChain] && <span className="ml-auto">({balanceByChain[selectedChain]} USDC)</span>}
            </div>
            <div className="group-data-[state=open]:-scale-y-100 transition-transform">
              <ArrowIcon direction="down" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0">
          <ScrollArea className="flex max-h-36 flex-col overflow-y-auto">
            <PopoverList>
              {chains.map((chain) => {
                return (
                  <PopoverListItem key={chain}>
                    <Button
                      variant="default"
                      size="default"
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-2',
                        'rounded-xl hocus:bg-border ring-inset',
                        selectedChain === chain && 'font-bold text-heading-foreground'
                      )}
                      onClick={() => onChainChange(chain)}
                    >
                      <div className="flex w-full items-center gap-2">
                        <NetworkImage network={chain} className="size-6" />
                        <span className="capitalize">{getNetworkName(chain)}</span>
                        {balanceByChain[chain] && (
                          <span className="ml-auto text-sm">({balanceByChain[chain]} USDC)</span>
                        )}
                      </div>
                      <CheckIcon
                        size={16}
                        className={cn('text-success opacity-0', selectedChain === chain && 'opacity-100')}
                      />
                    </Button>
                  </PopoverListItem>
                )
              })}
            </PopoverList>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}
